import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationService } from '../services/notifications';
import { IUserNotification } from '../types/notification';
import { useClicksterStore } from '../stores';

const NOTIFICATIONS_QUERY_KEY = 'user-notifications';
const SEEN_NOTIFICATIONS_QUERY_KEY = 'seen-user-notifications';
const UNSEEN_COUNT_QUERY_KEY = 'unseen-notifications-count';

/**
 * Hook to manage user notifications
 * Provides notifications data, unseen count, and mutation methods
 */
export const useNotifications = () => {
  const queryClient = useQueryClient();
  const { userInSession } = useClicksterStore((state) => state);
  const userId = userInSession?._id;

  const {
    data: notifications = [],
    isLoading: isLoadingNotifications,
    refetch: refetchNotifications,
  } = useQuery<IUserNotification[]>(
    [NOTIFICATIONS_QUERY_KEY, userId],
    () => NotificationService.getUserNotifications(userId!, false),
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchInterval: 1000 * 60 * 5, // Poll every 5 minutes
    },
  );

  // Fetch seen (read) notifications
  const {
    data: readNotifications = [],
    isLoading: isLoadingReadNotifications,
    refetch: refetchReadNotifications,
  } = useQuery<IUserNotification[]>(
    [SEEN_NOTIFICATIONS_QUERY_KEY, userId],
    () => NotificationService.getUserNotifications(userId!, true),
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchInterval: 1000 * 60 * 5, // Poll every 5 minutes
    },
  );

  // Fetch unseen count
  const {
    data: unseenCount = 0,
    isLoading: isLoadingCount,
    refetch: refetchCount,
  } = useQuery<number>(
    [UNSEEN_COUNT_QUERY_KEY, userId],
    () => NotificationService.getUnseenCount(userId!),
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchInterval: 1000 * 60 * 5, // Poll every 5 minutes
    },
  );

  // Mark single notification as seen
  const markAsSeenMutation = useMutation(
    ({ notificationId }: { notificationId: string }) =>
      NotificationService.markAsSeen(notificationId, userId!),
    {
      onSuccess: () => {
        // Invalidate and refetch all notification queries
        queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([SEEN_NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([UNSEEN_COUNT_QUERY_KEY, userId]);
      },
    },
  );

  // Mark all notifications as seen
  const markAllAsSeenMutation = useMutation(
    () => NotificationService.markAllAsSeen(userId!),
    {
      onSuccess: () => {
        // Invalidate and refetch all notification queries
        queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([SEEN_NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([UNSEEN_COUNT_QUERY_KEY, userId]);
      },
    },
  );

  // Mark single notification as deleted
  const markAsDeletedMutation = useMutation(
    ({ notificationId }: { notificationId: string }) =>
      NotificationService.markAsDeleted(notificationId, userId!),
    {
      onSuccess: () => {
        // Invalidate and refetch all notification queries
        queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([SEEN_NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([UNSEEN_COUNT_QUERY_KEY, userId]);
      },
    },
  );

  // Mark all notifications as deleted
  const markAllAsDeletedMutation = useMutation(
    () => NotificationService.markAllAsDeleted(userId!),
    {
      onSuccess: () => {
        // Invalidate and refetch all notification queries
        queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([SEEN_NOTIFICATIONS_QUERY_KEY, userId]);
        queryClient.invalidateQueries([UNSEEN_COUNT_QUERY_KEY, userId]);
      },
    },
  );

  return {
    notifications,
    readNotifications,
    unseenCount,
    seenCount: readNotifications.length,
    isLoading: isLoadingNotifications || isLoadingCount,
    isLoadingNotifications,
    isLoadingReadNotifications,
    isLoadingCount,
    markAsSeen: (notificationId: string) =>
      markAsSeenMutation.mutateAsync({ notificationId }),
    markAllAsSeen: () => markAllAsSeenMutation.mutateAsync(),
    markAsDeleted: (notificationId: string) =>
      markAsDeletedMutation.mutateAsync({ notificationId }),
    markAllAsDeleted: () => markAllAsDeletedMutation.mutateAsync(),
    refetchNotifications,
    refetchReadNotifications,
    refetchCount,
  };
};
